import React, { useState, useEffect, useCallback } from "react";
import { Image as ImageIcon, Download, ShoppingBag, Star, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { API_URL } from "../../constants";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";
import { bakeDisclaimerOnBase64 } from "../../utils/aiDisclaimer";

export const MyDecorations = ({ phone, getAuthHeaders }) => {
  const [gallery, setGallery] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [viewingImage, setViewingImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const fetchGallery = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders(), "x-customer-phone": phone || "" };
      const [galRes, loyRes] = await Promise.all([
        fetch(`${API_URL}/api/magic/gallery`, { headers }),
        fetch(`${API_URL}/api/magic/loyalty`, { headers }),
      ]);
      const galData = await galRes.json();
      const loyData = await loyRes.json();
      setGallery(galData.images || []);
      setLoyalty(loyData);
    } catch (e) { console.debug('[MyDecorations] Gallery fetch failed:', e.message); }
    setLoading(false);
  }, [phone, getAuthHeaders]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleViewImage = async (imageId) => {
    setLoadingImage(true);
    try {
      const headers = { ...getAuthHeaders(), "x-customer-phone": phone || "" };
      const res = await fetch(`${API_URL}/api/magic/gallery/${imageId}`, { headers });
      const data = await res.json();
      if (data.image_base64) setViewingImage(data);
    } catch (e) { console.debug('[MyDecorations] Image load failed:', e.message); }
    setLoadingImage(false);
  };

  const handleDownload = async (img) => {
    if (!img?.image_base64) return;
    const dataUrl = await bakeDisclaimerOnBase64(img.image_base64, "image/jpeg");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `doozygo-${img.package}-${img.occasion}.jpg`;
    link.click();
  };

  if (loading) return null;
  if (gallery.length === 0 && !loyalty?.eligible) return null;

  return (
    <>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] overflow-hidden" data-testid="my-decorations">
        {/* Header */}
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-5">
          <h3 className="text-sm font-semibold text-[#1E1B4B] uppercase tracking-wider flex items-center gap-2">
            <ImageIcon size={16} className="text-[#E83E8C]" /> My Decorations ({gallery.length})
          </h3>
          {expanded ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
        </button>

        {expanded && (
          <div className="px-5 pb-5">
            {/* Loyalty Status Banner */}
            {loyalty && (
              <div className={`rounded-xl p-3 mb-4 text-sm ${loyalty.eligible ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`} data-testid="loyalty-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Star size={14} className={loyalty.eligible ? "text-amber-500" : "text-gray-400"} />
                  <span className="font-semibold text-[#1E1B4B] text-xs">
                    {loyalty.eligible ? "Loyalty Credit Unlocked!" : "Loyalty Program"}
                  </span>
                </div>
                <p className="text-xs text-[#475569]">{loyalty.message}</p>
                {loyalty.credit > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">Rs {loyalty.credit} credit</span>
                    <span className="text-[10px] text-[#94a3b8]">Adjusted on your next booking</span>
                  </div>
                )}
                {!loyalty.eligible && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 text-[10px] text-[#94a3b8]">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-[#E83E8C] rounded-full transition-all" style={{ width: `${Math.min(100, (loyalty.paid_generations / loyalty.threshold) * 100)}%` }} />
                      </div>
                      <span>{loyalty.paid_generations}/{loyalty.threshold}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Image Grid */}
            {gallery.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {gallery.map(img => (
                  <button
                    key={img.image_id}
                    onClick={() => handleViewImage(img.image_id)}
                    className="relative group rounded-xl overflow-hidden border border-[#f0e6d9] hover:border-[#E83E8C] transition-all shadow-sm hover:shadow-md"
                    data-testid={`gallery-img-${img.image_id}`}
                  >
                    <div className="aspect-[4/3] bg-gradient-to-br from-[#E83E8C]/10 to-purple-100 flex items-center justify-center">
                      <ImageIcon size={24} className="text-[#E83E8C]/30" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-[10px] font-semibold">{img.package}</p>
                      <p className="text-white/70 text-[9px]">{img.occasion} • {img.created_at?.split("T")[0]}</p>
                    </div>
                    {img.is_paid && (
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-amber-500/90 text-white text-[8px] font-bold">PAID</div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <ImageIcon size={28} className="mx-auto mb-2 text-[#E83E8C]/20" />
                <p className="text-xs text-[#94a3b8]">No decorations generated yet.</p>
                <a href="/doozygo-magic" className="text-xs text-[#E83E8C] font-medium hover:underline mt-1 inline-block">Try DoozyGo Magic</a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full Image Viewer Modal */}
      {viewingImage && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#E83E8C]/5 to-purple-50 border-b">
              <span className="text-sm font-semibold text-[#1E1B4B]">{viewingImage.package} {viewingImage.occasion}</span>
              <span className="text-[10px] text-[#94a3b8]">{viewingImage.created_at?.split("T")[0]}</span>
            </div>
            {loadingImage ? (
              <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#E83E8C]" /></div>
            ) : (
              <AIImageWithDisclaimer src={`data:image/jpeg;base64,${viewingImage.image_base64}`} alt="Decoration" className="w-full block" />
            )}
            <div className="flex items-center gap-3 px-4 py-3 border-t">
              <button onClick={() => handleDownload(viewingImage)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E83E8C] text-white text-xs font-semibold hover:bg-[#d1357e]" data-testid="gallery-download-btn">
                <Download size={14} /> Download
              </button>
              <a href={`/?scrollTo=book-online&package=${encodeURIComponent(viewingImage.package)}&occasion=${encodeURIComponent(viewingImage.occasion)}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#25D366] text-white text-xs font-semibold hover:bg-[#1da851]" data-testid="gallery-book-btn">
                <ShoppingBag size={14} /> Book This Look
              </a>
              <button onClick={() => setViewingImage(null)} className="ml-auto text-xs text-[#94a3b8] hover:text-[#475569]">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
